import {
  useContext, useEffect, useState, CSSProperties,
} from 'react';
import styled, { FlattenInterpolation, ThemeProps } from 'styled-components';
import _ from 'lodash';
import { allCountries } from './allCountries';
import { useDelayCall } from '../../../../util';
import {
  AddressType, AddressSearchType, useAddressSearchApi, useAddressFindByIdApi, useAddressFindByPostCodeApi, CountryType, AddressFindByIdType,
} from '../../../../api';
import {
  useModelForm, ModelFormContext, FormInput,
} from '../ModelForm';
import { Flex, FlexColumn } from '../../../Flex';
import { ThemeType } from '../../../../styling';
import {
  TextInput,
  SelectInput,
  InputLabel,
  SearchListBox,
} from '../../inputs';
import {
  required, ValidationFunction, maxLength, ukPostcode, nonWhitespace, mandatory, securityAddressPostcode,
} from '../../../../validator';
import { AlertError, FormLayout } from '../../../../..';

const AddressForm = styled(FlexColumn) <{ cols?: number, css?: FlattenInterpolation<ThemeProps<ThemeType>> | string }>`
  gap: ${({ theme }) => theme.padding[7]};
  width: 100%;
`;

const StyledRelativeWrapper = styled(Flex)`
  position: relative;
  flex-direction: column;
`;

interface ModelAddressType<T> {
  houseNumber: T;
  houseName: T;
  subBuilding: T;
  postTown: T;
  street: T;
  postCode: T;
  country: T;
  region: T;
  houseNumberAndHouseNameMissing: T;
}

export interface ModelAddressDisableType extends ModelAddressType<boolean> { }
export interface ModelAddressTouchOverrideType extends ModelAddressType<boolean> { }
export interface ModelAddressErrorOverrideType extends ModelAddressType<string> { }
export interface ModelAddressLabelType extends ModelAddressType<string> {
  findAddress: string,
}

export interface ModelAddressRequiredType extends ModelAddressType<boolean> { }
export interface ModelAddressOnVisibleChangesType extends ModelAddressType<(b: boolean) => void> { }

export const UKCountries: Record<string, string> = {
  'GB-ENG': 'England',
  'GG': 'Guernsey',
  'JE': 'Jersey',
  'GB-NIR': 'Northern Ireland',
  'GB-SCT': 'Scotland',
  'GB-WLS': 'Wales',
};

export const getAddressRule = (country: string, houseNumber: string, subBuilding: string, houseName: string): {
  [key: string]: ValidationFunction[],
} => {
  const initialRules = {
    postCode: [mandatory('Please enter a postal code'), maxLength(10)],
    country: [mandatory('Please select a country')],
    houseNumber: [maxLength(32), nonWhitespace('house or building number')],
    houseName: [maxLength(32), nonWhitespace('house or building name')],
    subBuilding: [maxLength(32), nonWhitespace('flat number')],
    postTown: [maxLength(28), nonWhitespace('town or city name')],
    street: [maxLength(32), nonWhitespace('street name')],
  };

  if (country === 'GB-ENG' || country === 'GB-SCT' || country === 'GB-WLS' || country === 'GB-NIR') {
    initialRules.postCode.push(ukPostcode);
  }

  if (houseNumber || subBuilding) {
    initialRules.street.push(mandatory('Please enter a street name'));
  }

  if (!houseNumber && !houseName) {
    initialRules.houseNumber.push(required);
    initialRules.houseName.push(required);
  }

  return initialRules;
};

export function ModelAddress({
  id,
  fields,
  labelStyles = '',
  valueOverride,
  labelOverride = {},
  placeholderOverride = {
    findAddress: '',
    houseNumber: '',
    houseName: '',
    subBuilding: '',
    postTown: '',
    street: '',
    postCode: '',
    country: '',
    region: '',
  },
  disabledOverride = {},
  touchOverride,
  errorOverride,
  mutateValue = (newValue) => newValue,
  disabled = false,
  layoutCols = 2,
  visibleSearchBox = true,
  onVisibleChanges,
  layoutStyles = {},
  limitToGreatBritain = false,
  isSecurityAddress = false,
  isCopyCurrentAddress = false,
  limit6UKCountries = false,
}: {
  id: string,
  fields: AddressType,
  labelStyles?: string,
  valueOverride?: Partial<AddressType>,
  labelOverride?: Partial<ModelAddressLabelType>,
  placeholderOverride?: Partial<ModelAddressLabelType>,
  disabledOverride?: Partial<ModelAddressDisableType>,
  touchOverride?: Partial<ModelAddressTouchOverrideType>,
  errorOverride?: Partial<ModelAddressErrorOverrideType>,
  onVisibleChanges?: Partial<ModelAddressOnVisibleChangesType>
  mutateValue?: (newValue: Record<string, string>) => any,
  disabled?: boolean,
  layoutCols?: number,
  visibleSearchBox?: boolean,
  layoutStyles?: CSSProperties,
  limitToGreatBritain?: boolean,
  isSecurityAddress?: boolean,
  isCopyCurrentAddress?: boolean,
  limit6UKCountries?: boolean,
}) {
  const { update } = useContext(ModelFormContext);

  const address = {
    houseNumber: useModelForm(fields.houseNumber, valueOverride?.houseNumber),
    houseName: useModelForm(fields.houseName, valueOverride?.houseName),
    subBuilding: useModelForm(fields.subBuilding, valueOverride?.subBuilding),
    postTown: useModelForm(fields.postTown, valueOverride?.postTown),
    street: useModelForm(fields.street, valueOverride?.street),
    postCode: useModelForm(fields.postCode, valueOverride?.postCode),
    country: useModelForm(fields.country, valueOverride?.country),
    region: useModelForm(fields.region, valueOverride?.region),
  };

  const [search, setSearch] = useState<string>('');
  const [searchAddresses, setSearchAddresses] = useState<AddressSearchType[]>([]);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [showAlertPostCode, setShowAlertPostCode] = useState<boolean>(false);
  const [isDisableCountry, setIsDisableCountry] = useState<boolean>(isSecurityAddress);

  const [postCodeCountries, setPostCodeCountries] = useState<{ [key: string]: string; }>(UKCountries);
  const [isFocusPostCode, setIsFocusPostCode] = useState<boolean>(false);
  const [regionsData, setRegionsData] = useState<any>({});

  const addressSearchApi = useAddressSearchApi();
  const addressFindByIdApi = useAddressFindByIdApi();
  const addressFindByPostCodeApi = useAddressFindByPostCodeApi();

  useDelayCall(() => {
    const fetchData = async () => {
      const data = await addressSearchApi.get(search, limitToGreatBritain);
      setSearchAddresses(data);
    };
    fetchData();
  }, [search]);

  const isValidUKPostCode = (postCodeValue: string): boolean => ukPostcode.validator(postCodeValue);
  const isValidSecurityAddressPostcode = (postCodeValue: string): boolean => securityAddressPostcode.validator(postCodeValue);

  const handleDataCountries = (matchedCountries: CountryType[]) => {
    const countriesOptions = matchedCountries?.reduce((obj, item) => Object.assign(obj, { [item?.countryIsoCode]: item?.country }), {});
    return countriesOptions ?? {};
  };

  const updateAddress = (field: string, newValue: string) => {
    update(mutateValue({
      ...valueOverride,
      [field]: _.isEmpty(newValue) ? undefined : newValue,
    }));
  };

  const mapAddressData = (data: AddressFindByIdType) => (
    Object.entries(data).reduce((prev, [k, v]) => {
      let value = v;
      if (k === 'country') {
        value = data.countryIsoCode;
      }

      return {
        ...prev,
        [fields[k as keyof AddressType]]: value,
      };
    }, {})
  );

  const drivenCountryByPostcode = (matchedCountries: CountryType[], countryIsoCode: string, matchedRegions: { [key: string]: string[] }) => {
    const countriesFormatted = handleDataCountries(matchedCountries);
    updateAddress(fields.country, countryIsoCode);
    setPostCodeCountries(countriesFormatted);
    setRegionsData(matchedRegions);
    setIsDisableCountry(matchedCountries.length <= 1);
  };

  const onChangeAddressId = async (newAddressId?: string) => {
    if (newAddressId) {
      const data: AddressFindByIdType = await addressFindByIdApi.get(newAddressId);
      const {
        countryIsoCode, matchedCountries, matchedRegions,
      } = data;

      const dataMap = mapAddressData(data);
      update(mutateValue(dataMap));

      if (isSecurityAddress) {
        drivenCountryByPostcode(matchedCountries, countryIsoCode, matchedRegions);
      }
    }
  };

  // Load countries, regions when changing postCode
  const onChangePostCode = async (postCodeValue: string) => {
    updateAddress(fields.postCode, postCodeValue);
    if (isSecurityAddress) {
      if (postCodeValue && isValidUKPostCode(postCodeValue) && isValidSecurityAddressPostcode(postCodeValue)) {
        const addressFind = await addressFindByPostCodeApi.get(postCodeValue);
        const {
          countryIsoCode, region, matchedCountries, matchedRegions,
        } = addressFind;

        drivenCountryByPostcode(matchedCountries, countryIsoCode, matchedRegions);
        updateAddress(fields.region, region);
      } else {
        updateAddress(fields.country, '');
        setIsDisableCountry(true);
      }
    }
  };

  // Load countries, regions by postCode when did mount
  const onFirstLoadPostCode = async () => {
    if (address.postCode.value && isSecurityAddress && isValidUKPostCode(address.postCode.value)) {
      const addressFind = await addressFindByPostCodeApi.get(address.postCode.value);
      const {
        matchedCountries, matchedRegions, countryIsoCode,
      } = addressFind;
      drivenCountryByPostcode(matchedCountries, countryIsoCode, matchedRegions);
    }
  };

  // Load countries, regions when copying current address
  const onCopyCurrentAddress = async () => {
    const addressFind = await addressFindByPostCodeApi.get(address.postCode.value);
    const {
      countryIsoCode, region, matchedCountries, matchedRegions,
    } = addressFind;

    drivenCountryByPostcode(matchedCountries, countryIsoCode, matchedRegions);
    updateAddress(fields.region, region);
  };

  useEffect(() => {
    if (isCopyCurrentAddress && !isFocusPostCode) {
      // Only run this when copying current address
      const fetchRegionsData = async () => {
        await onCopyCurrentAddress();
      };
      fetchRegionsData();
    }
  }, [address.postCode.value]);

  useEffect(() => {
    if (isSecurityAddress) {
      const fetchRegionsData = async () => {
        await onFirstLoadPostCode();
      };
      fetchRegionsData();
    } else {
      setIsDisableCountry(false);
    }
  }, [isSecurityAddress]);

  // On change country
  const onChangeCountry = (newValue: string) => {
    updateAddress(fields.country, newValue);
    if (isSecurityAddress) {
      updateAddress(fields.region, regionsData[newValue]?.[0] ?? '');
    }
  };

  useEffect(() => onVisibleChanges?.houseNumber?.(address.houseNumber.visible), [address.houseNumber.visible]);
  useEffect(() => onVisibleChanges?.subBuilding?.(address.subBuilding.visible), [address.subBuilding.visible]);
  useEffect(() => onVisibleChanges?.houseName?.(address.houseName.visible), [address.houseName.visible]);
  useEffect(() => onVisibleChanges?.postTown?.(address.postTown.visible), [address.postTown.visible]);
  useEffect(() => onVisibleChanges?.street?.(address.street.visible), [address.street.visible]);
  useEffect(() => onVisibleChanges?.postCode?.(address.postCode.visible), [address.postCode.visible]);
  useEffect(() => onVisibleChanges?.country?.(address.country.visible), [address.country.visible]);

  let countryOptions = allCountries;
  if (limit6UKCountries) {
    countryOptions = UKCountries;
  } else if (isSecurityAddress) {
    countryOptions = postCodeCountries;
  }

  const renderFlatNumber = (
    <FormInput visible={address.subBuilding.visible}>
      <StyledRelativeWrapper>
        <InputLabel required>
          {labelOverride.subBuilding || 'Flat number'}
        </InputLabel>
        <TextInput
          id={`${fields.subBuilding}`}
          placeholder={placeholderOverride.subBuilding}
          value={address.subBuilding.value}
          errors={[errorOverride?.subBuilding || address.subBuilding.errors[0]]}
          visible={address.subBuilding.visible}
          onChange={(newValue: string) => updateAddress(fields.subBuilding, newValue)}
          disabled={disabled || disabledOverride.subBuilding}
          touchOverride={touchOverride?.subBuilding}
        />
      </StyledRelativeWrapper>
    </FormInput>
  );
  const renderStreet = (
    <FormInput visible={address.street.visible}>
      <StyledRelativeWrapper>
        <InputLabel required>
          {labelOverride.street || 'Street'}
        </InputLabel>
        <TextInput
          id={`${fields.street}`}
          placeholder={placeholderOverride.street}
          value={address.street.value}
          errors={[errorOverride?.street || address.street.errors[0]]}
          visible={address.street.visible}
          onChange={(newValue: string) => updateAddress(fields.street, newValue)}
          disabled={disabled || disabledOverride.street}
          touchOverride={touchOverride?.street}
        />
      </StyledRelativeWrapper>
    </FormInput>
  );
  const renderHouseNumber = (
    <FormInput visible={address.houseNumber.visible}>
      <StyledRelativeWrapper>
        <InputLabel required>
          {labelOverride.houseNumber || 'House / building number'}
        </InputLabel>
        <TextInput
          id={`${fields.houseNumber}`}
          placeholder={placeholderOverride.houseNumber}
          labelStyles={labelStyles}
          value={address.houseNumber.value}
          errors={[errorOverride?.houseNumber || errorOverride?.houseNumberAndHouseNameMissing || address.houseNumber.errors[0]]}
          visible={address.houseNumber.visible}
          onChange={(newValue: string) => updateAddress(fields.houseNumber, newValue)}
          disabled={disabled || disabledOverride.houseNumber}
          hideErrorMessage={address.houseNumber.errors[0] === 'Required' || !!errorOverride?.houseNumberAndHouseNameMissing}
          onBlur={() => setShowAlert(true)}
          touchOverride={touchOverride?.houseNumber}
        />
      </StyledRelativeWrapper>
    </FormInput>
  );
  const renderPostTown = (
    <FormInput visible={address.postTown.visible}>
      <StyledRelativeWrapper>
        <InputLabel required>
          {labelOverride.postTown || 'Town / city'}
        </InputLabel>
        <TextInput
          id={`${fields.postTown}`}
          placeholder={placeholderOverride.postTown}
          value={address.postTown.value}
          errors={[errorOverride?.postTown || address.postTown.errors[0]]}
          visible={address.postTown.visible}
          onChange={(newValue: string) => updateAddress(fields.postTown, newValue)}
          disabled={disabled || disabledOverride.postTown}
          touchOverride={touchOverride?.postTown}
        />
      </StyledRelativeWrapper>
    </FormInput>
  );
  const renderHouseName = (
    <FormInput visible={address.houseName.visible}>
      <StyledRelativeWrapper>
        <InputLabel required>
          {labelOverride.houseName || 'House / building name'}
        </InputLabel>
        <TextInput
          id={`${fields.houseName}`}
          placeholder={placeholderOverride.houseName}
          value={address.houseName.value}
          errors={[errorOverride?.houseName || errorOverride?.houseNumberAndHouseNameMissing || address.houseName.errors[0]]}
          visible={address.houseName.visible}
          onChange={(newValue: string) => updateAddress(fields.houseName, newValue)}
          disabled={disabled || disabledOverride.houseName}
          hideErrorMessage={address.houseName.errors[0] === 'Required' || !!errorOverride?.houseNumberAndHouseNameMissing}
          onBlur={() => setShowAlert(true)}
          touchOverride={touchOverride?.houseName}
        />
      </StyledRelativeWrapper>
    </FormInput>
  );
  const renderPostCode = (
    <FormInput visible={address.postCode.visible}>
      <TextInput
        id={`${fields.postCode}`}
        placeholder={placeholderOverride.postCode}
        label={labelOverride.postCode || 'Postcode'}
        value={address.postCode.value}
        errors={[errorOverride?.postCode || address.postCode.errors[0]]}
        visible={address.postCode.visible}
        onChange={(newValue: string) => onChangePostCode(newValue)}
        disabled={disabled || disabledOverride.postCode}
        required
        hideErrorMessage={address.postCode.errors[0] === 'We\'re unable to process applications with this postal code'}
        touchOverride={touchOverride?.postCode}
        onFocus={() => setIsFocusPostCode(true)}
        onBlur={() => { setIsFocusPostCode(false); setShowAlertPostCode(true); }}
      />
    </FormInput>
  );

  const renderCountry = (
    <FormInput visible={address.country.visible}>
      <SelectInput
        options={countryOptions}
        id={`${fields.country}`}
        placeholder={placeholderOverride.country}
        label={labelOverride.country || 'Country'}
        labelStyles={labelStyles}
        value={address.country.value}
        errors={isDisableCountry ? [] : [errorOverride?.country || address.country.errors[0]]}
        onChange={(newValue: string) => onChangeCountry(newValue)}
        disabled={disabled || disabledOverride.country || isDisableCountry}
        required
        touchOverride={touchOverride?.country}
      />
    </FormInput>
  );
  return (
    <AddressForm>
      <FormInput visible={visibleSearchBox && !disabled}>
        <SearchListBox
          id={`${id}-search-list-box`}
          searchValue={search}
          searchPlaceholder={placeholderOverride.findAddress || 'Enter postcode or first line of registered address'}
          label={labelOverride.findAddress || 'Find address'}
          labelStyles={labelStyles}
          options={searchAddresses.reduce((prev, current) => ({
            ...prev,
            [current.id]: current.text,
          }), {})}
          onChangeSearch={setSearch}
          onChangeItem={onChangeAddressId}
          disabled={disabled}
          required
        />
      </FormInput>

      <FormLayout cols={layoutCols} style={layoutStyles}>
        {renderFlatNumber}
        {renderPostTown}
        {renderHouseNumber}
        {renderPostCode}
        {renderHouseName}
        {renderCountry}
        {renderStreet}
      </FormLayout>
      {(address.postCode.errors[0] === 'We\'re unable to process applications with this postal code' && (showAlertPostCode || touchOverride?.postCode)) && (
        <AlertError>{address.postCode.errors[0]}</AlertError>
      )}
      {(address.houseName.errors[0] === 'Required' || errorOverride?.houseNumberAndHouseNameMissing) && (touchOverride?.houseName || showAlert) && (
        <AlertError>Please enter the house / building number or name.</AlertError>
      )}
    </AddressForm>
  );
}
